import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { StatusPill } from '@/components/StatusPill';

// The gateway's public front door (APIM). Every endpoint lives under /api/v1.
const BASE_URL = 'https://keytravelapim.azure-api.net/ai';
// Entra scope an app acquires a token for. The portal does this for you via MSAL.
const GATEWAY_SCOPE = 'api://ffcdb9dc-0b1d-4479-8c5b-15a71c216098/access_as_user';

const curlSnippet = `curl -X POST ${BASE_URL}/api/v1/completions \\
  -H "Authorization: Bearer $TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "supplier": "Anthropic",
    "modelId": "claude-sonnet-5",
    "messages": [{ "role": "User", "content": "Say hello in one sentence." }],
    "maxTokens": 200,
    "piiMode": "None"
  }'

# System / service callers also send the subscription key an admin gave them:
#   -H "Ocp-Apim-Subscription-Key: <your-key>"`;

const csharpSnippet = `// dotnet add package KeyTravel.ArtificialIntelligence.Client
// Register once (the gateway runs in user context, so a JWT is always required):
services.AddKeyTravelArtificialIntelligenceClient(configuration)
        .WithJwtBearer(async ct => (await credential.GetTokenAsync(scope, ct)).Token);

// Then inject ArtificialIntelligenceApiClient and call it:
public class Greeter(ArtificialIntelligenceApiClient ai)
{
    public async Task<string> HelloAsync()
    {
        var result = await ai.CompleteAsync(
            AiSuppliers.Anthropic, "claude-sonnet-5",
            userMessage: "Say hello in one sentence.");

        return result.Content; // result.Usage.EstimatedCostUsd is there too
    }
}`;

const tsSnippet = `// npm install @keytravel/artificial-intelligence-client
import { ArtificialIntelligenceClient } from '@keytravel/artificial-intelligence-client';

const ai = new ArtificialIntelligenceClient({
  // Return an Entra access token for the gateway scope (thin wrapper over MSAL).
  getAccessToken: () => acquireToken(),
  // baseUrl defaults to ${BASE_URL}
  // subscriptionKey: '<your-key>'  // system / service callers only
});

const result = await ai.completeText(
  'Anthropic', 'claude-sonnet-5', 'Say hello in one sentence.');

console.log(result.content);`;

const styles = `
.gs-page { max-width: 900px; }
.gs-step {
  width: 24px; height: 24px; flex-shrink: 0;
  background: var(--kt-green-light); color: var(--kt-green-dark);
  border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: .8rem;
}
.gs-page p { color: var(--kt-grey-700); margin-bottom: 10px; }
.gs-page p:last-child { margin-bottom: 0; }
.gs-codewrap { position: relative; }
.gs-code {
  background: var(--kt-green-dark); color: var(--kt-bar-fg);
  border-radius: var(--radius-sm); padding: 14px 16px; margin: 8px 0 4px;
  font-family: ui-monospace, Consolas, monospace; font-size: .82rem; line-height: 1.55;
  overflow-x: auto; white-space: pre;
}
.gs-code code { font-family: inherit; }
.gs-copy {
  position: absolute; top: 8px; right: 8px; z-index: 1;
  background: rgba(255,255,255,.12); color: var(--kt-bar-fg);
  border: 1px solid rgba(255,255,255,.25); border-radius: var(--radius-sm);
  font-size: .74rem; font-weight: 600; padding: 4px 9px;
}
.gs-copy:hover { background: rgba(255,255,255,.22); }
.gs-kbd {
  font-family: ui-monospace, Consolas, monospace; font-size: .82em;
  background: var(--kt-grey-100); border: 1px solid var(--kt-grey-200);
  border-radius: 4px; padding: 1px 5px; color: var(--kt-heading); white-space: nowrap;
}
.gs-tabbar { margin-bottom: 6px; }
.gs-concepts { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.gs-concept {
  border: 1px solid var(--kt-grey-200); border-radius: var(--radius-sm);
  padding: 12px 14px; background: var(--kt-grey-50);
}
.gs-concept h4 { font-size: .92rem; color: var(--kt-heading); margin-bottom: 4px; }
.gs-concept p { font-size: .85rem; color: var(--kt-grey-700); margin-bottom: 6px; }
.gs-pkg { margin-bottom: 16px; }
.gs-pkg:last-child { margin-bottom: 0; }
.gs-pkg h4 { font-size: .95rem; color: var(--kt-heading); margin-bottom: 6px; }
.gs-note {
  font-size: .8rem; color: var(--kt-grey-500);
  border-left: 3px solid var(--kt-orange); padding: 3px 0 3px 10px; margin-top: 8px;
}
.gs-ul { margin: 6px 0 10px 18px; }
.gs-ul li { margin-bottom: 6px; color: var(--kt-grey-700); font-size: .9rem; }
.gs-status { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.gs-status p { margin: 0; }
@media (max-width: 760px) { .gs-concepts { grid-template-columns: 1fr; } }
`;

export function GetStarted() {
  const api = useApi();
  // Shares the ['me'] cache with the rest of the app; the guide renders regardless.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });

  // Deep links from the sidebar ("API reference" -> #step-7): the router doesn't scroll to a
  // hash on its own, so do it once the sections are on the page.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash]);

  return (
    <div className="gs-page">
      <style>{styles}</style>

      <div className="page-header">
        <div>
          <h1>Get started</h1>
          <div className="sub">How to go from signed-in to your first AI call, in a few minutes.</div>
        </div>
      </div>

      {me && <AccessCallout status={me.registrationStatus} />}

      <Section n={1} title="What this is">
        <p>
          The AI Gateway is Key Travel's single, governed front door to the big language models
          &mdash; Claude (Anthropic), OpenAI, Azure OpenAI, DeepSeek, MiniMax, Kimi and Grok. Instead
          of every app juggling its own supplier keys, you send one authenticated request here and the
          gateway supplies the key, meters your usage in dollars, enforces a monthly budget, can strip
          PII before anything leaves Key Travel, and logs every call. One login, one endpoint, many models.
        </p>
      </Section>

      <Section n={2} title="Getting access">
        <p>You need an approved registration before the gateway will answer a single request.</p>
        <ul className="gs-ul">
          <li>Open the <Link to="/">Home</Link> page and hit <strong>Request access</strong> (a line on what you'll use it for helps).</li>
          <li>An admin approves you and sets your <strong>monthly budget</strong>.</li>
          <li>Until your status is <strong>Active</strong> you can browse this portal, but calls are refused with <code className="gs-kbd">caller_not_active</code>.</li>
        </ul>
        <p>Gateway administrators are approved automatically.</p>
      </Section>

      <Section n={3} title="Authenticating">
        <p>
          Every call needs an <strong>Entra ID token</strong> &mdash; the gateway always runs as
          <em> you</em>, never as an anonymous service.
        </p>
        <ul className="gs-ul">
          <li><strong>In this portal:</strong> nothing to do. You're signed in and the app attaches your token for you.</li>
          <li>
            <strong>From your own app:</strong> acquire an Entra access token for the gateway scope{' '}
            <code className="gs-kbd">{GATEWAY_SCOPE}</code> (e.g. MSAL <code className="gs-kbd">acquireTokenSilent</code>)
            and send it as <code className="gs-kbd">Authorization: Bearer &lt;token&gt;</code>.
          </li>
          <li>
            <strong>System / service callers:</strong> an admin registers an APIM subscription for you.
            Send its key as <code className="gs-kbd">Ocp-Apim-Subscription-Key: &lt;key&gt;</code> alongside your
            token &mdash; the subscription is what identifies your system as a caller for budgeting and reporting.
          </li>
        </ul>
        <p>
          Base URL: <code className="gs-kbd">{BASE_URL}</code> &mdash; every endpoint lives under{' '}
          <code className="gs-kbd">/api/v1</code>.
        </p>
      </Section>

      <Section n={4} title="Your first call">
        <p>
          A minimal chat completion is a <code className="gs-kbd">POST</code> to{' '}
          <code className="gs-kbd">{BASE_URL}/api/v1/completions</code>. Here it is three ways &mdash;
          pick your stack:
        </p>
        <FirstCallTabs />
        <p style={{ marginTop: 10 }}>
          The reply comes back with the text in <code className="gs-kbd">content</code>, plus{' '}
          <code className="gs-kbd">usage</code> (tokens and <code className="gs-kbd">estimatedCostUsd</code>) and a{' '}
          <code className="gs-kbd">correlationId</code> you'll see again in <Link to="/usage">My usage</Link>.
          Prefer to click before you code? Try it in the <Link to="/playground">Playground</Link>.
        </p>
      </Section>

      <Section n={5} title="Key concepts, briefly">
        <div className="gs-concepts">
          <div className="gs-concept">
            <h4>Models &amp; pricing</h4>
            <p>
              <code className="gs-kbd">GET /api/v1/models</code> lists what you're allowed to call, with
              per-1M-token pricing and capability flags. Admins keep the allow-list; every call is metered
              against your budget (a used-up budget returns <code className="gs-kbd">budget_exhausted</code>).
            </p>
            <Link to="/playground">Model picker &rarr;</Link>
          </div>
          <div className="gs-concept">
            <h4>Skills</h4>
            <p>
              Reusable, approved bundles of system prompt + reference docs + suggested settings. Apply one by
              setting <code className="gs-kbd">promptTemplateName</code> on a completion request.
            </p>
            <Link to="/templates">Skills &rarr;</Link>
          </div>
          <div className="gs-concept">
            <h4>PII modes</h4>
            <p>
              Set <code className="gs-kbd">piiMode</code>: <code className="gs-kbd">None</code>,{' '}
              <code className="gs-kbd">RedactAndRestore</code> (mask before the supplier, restore in the reply),
              or <code className="gs-kbd">Redact</code> (mask permanently). Covers text only.
            </p>
          </div>
          <div className="gs-concept">
            <h4>Attachments</h4>
            <p>
              Send images or PDFs on a message &mdash; but check the model's{' '}
              <code className="gs-kbd">supportsImageInput</code> / <code className="gs-kbd">supportsDocumentInput</code>{' '}
              flags first. Limits: 10&nbsp;MB per file, 25&nbsp;MB per request.
            </p>
            <Link to="/playground">Playground &rarr;</Link>
          </div>
          <div className="gs-concept">
            <h4>File tasks</h4>
            <p>
              Files in, files out: <code className="gs-kbd">POST /api/v1/file-tasks</code> runs real code in
              Anthropic's sandbox (e.g. "merge these spreadsheets and add a totals column"). Think minutes, not seconds.
            </p>
            <Link to="/file-tasks">File tasks &rarr;</Link>
          </div>
          <div className="gs-concept">
            <h4>Tools / MCP</h4>
            <p>
              Let the model call tools from MCP servers you've been granted; the tool loop runs server-side on the
              gateway. Use <code className="gs-kbd">tools.mcpServers</code>, keep <code className="gs-kbd">piiMode</code>{' '}
              None, and note access is default-deny. Servers that run as you take your own key: save it once in the
              Playground and later requests can omit <code className="gs-kbd">tools.credentials</code> entirely, or
              pass one to override the saved key for a single request.
            </p>
            <Link to="/playground">Playground &rarr;</Link>
          </div>
        </div>
      </Section>

      <Section n={6} title="The client packages">
        <p>Two typed, first-party clients wrap all of the above. Both live on Key Travel's Azure Artifacts feed.</p>

        <div className="gs-pkg">
          <h4>.NET &mdash; NuGet</h4>
          <p style={{ marginBottom: 6 }}>
            Package <code className="gs-kbd">KeyTravel.ArtificialIntelligence.Client</code> on the{' '}
            <code className="gs-kbd">keytravel-nuget</code> feed.
          </p>
          <Code text={`dotnet add package KeyTravel.ArtificialIntelligence.Client`} />
          <p style={{ margin: '8px 0 0' }}>
            Feed (add to <code className="gs-kbd">nuget.config</code>):{' '}
            <code className="gs-kbd">https://pkgs.dev.azure.com/keytravel/keytravel-artifacts/_packaging/keytravel-nuget/nuget/v3/index.json</code>
          </p>
          <p style={{ margin: '6px 0 0' }}>
            Browse:{' '}
            <a href="https://dev.azure.com/keytravel/keytravel-artifacts/_artifacts/feed/keytravel-nuget" target="_blank" rel="noreferrer">
              keytravel-nuget feed on Azure DevOps
            </a>
          </p>
          <div className="gs-note">
            The browse link is the standard Azure DevOps feed URL derived from the feed's index; confirm it opens
            the right feed the first time (it may prompt for Azure DevOps sign-in).
          </div>
        </div>

        <div className="gs-pkg">
          <h4>TypeScript / JavaScript &mdash; npm</h4>
          <p style={{ marginBottom: 6 }}>
            Package <code className="gs-kbd">@keytravel/artificial-intelligence-client</code> on the same feed's
            npm registry.
          </p>
          <Code
            text={`# .npmrc
@keytravel:registry=https://pkgs.dev.azure.com/keytravel/keytravel-artifacts/_packaging/keytravel-nuget/npm/registry/

npm install @keytravel/artificial-intelligence-client`}
          />
        </div>
      </Section>

      <Section n={7} title="Swagger / API reference">
        <p>
          The full endpoint reference is the OpenAPI (Swagger) document the API generates at{' '}
          <code className="gs-kbd">/swagger/v1/swagger.json</code>, which the pipeline imports into APIM as the{' '}
          <code className="gs-kbd">ai</code> API. The API host itself sits behind APIM (private), so the quickest way
          to prove your token and setup work end to end is a smoke-test call to the models endpoint:
        </p>
        <Code text={`GET ${BASE_URL}/api/v1/models`} />
        <p style={{ marginTop: 8 }}>
          Open{' '}
          <a href={`${BASE_URL}/api/v1/models`} target="_blank" rel="noreferrer">
            {`${BASE_URL}/api/v1/models`}
          </a>{' '}
          in a browser and you'll get a <code className="gs-kbd">401</code> &mdash; that's the gateway working and
          asking for your bearer token, not an outage. With a valid token it returns your model allow-list.
        </p>
        <div className="gs-note">
          The interactive Swagger UI (<code className="gs-kbd">/swagger</code>) is served by the API for local/dev
          runs; a public hosted Swagger UI URL, if published, would sit under the APIM <code className="gs-kbd">ai</code>{' '}
          path &mdash; confirm with the platform team.
        </div>
      </Section>
    </div>
  );
}

function AccessCallout({ status }: { status: string }) {
  const message =
    status === 'Active'
      ? "You're Active — you can make live calls right now. Jump to step 4 below, or open the Playground."
      : status === 'Pending'
      ? "Your access request is with an admin. Read on now; calls start working the moment you're Active."
      : status === 'Disabled'
      ? "Your access is disabled. Contact a gateway administrator if you think that's a mistake."
      : "You're not registered yet. Request access from the Home page to get going.";

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-body gs-status">
        <StatusPill status={status} />
        <p>{message}</p>
        {status === 'Active' ? (
          <Link to="/playground" className="btn-secondary btn-sm" style={{ marginLeft: 'auto' }}>Open playground</Link>
        ) : status === 'NotRegistered' ? (
          <Link to="/" className="btn-primary btn-sm" style={{ marginLeft: 'auto' }}>Request access</Link>
        ) : null}
      </div>
    </div>
  );
}

function Section({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <div className="card" id={`step-${n}`} style={{ marginBottom: 16, scrollMarginTop: 72 }}>
      <div className="card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span className="gs-step">{n}</span>
          <h3>{title}</h3>
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

const firstCallTabs = [
  { key: 'curl', label: 'cURL', code: curlSnippet },
  { key: 'csharp', label: 'C# (.NET)', code: csharpSnippet },
  { key: 'ts', label: 'TypeScript (npm)', code: tsSnippet }
];

function FirstCallTabs() {
  const [tab, setTab] = useState('curl');
  const active = firstCallTabs.find(t => t.key === tab) ?? firstCallTabs[0];
  return (
    <>
      <div className="filter-tabs gs-tabbar">
        {firstCallTabs.map(t => (
          <button
            key={t.key}
            type="button"
            className={`filter-tab ${t.key === tab ? 'on' : ''}`}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <Code text={active.code} />
    </>
  );
}

function Code({ text }: { text: string }) {
  return (
    <div className="gs-codewrap">
      <CopyButton text={text} />
      <pre className="gs-code"><code>{text}</code></pre>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="gs-copy"
      onClick={() => {
        navigator.clipboard?.writeText(text);
        setDone(true);
        window.setTimeout(() => setDone(false), 1500);
      }}>
      {done ? 'Copied' : 'Copy'}
    </button>
  );
}
